import { useRef, useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Eye, EyeOff, Maximize2, RotateCcw, Layers } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function VolumeViewer({ redVolume, greenVolume }) {
  const [showRed, setShowRed] = useState(true);
  const [showGreen, setShowGreen] = useState(true);
  const [redOpacity, setRedOpacity] = useState(0.8);
  const [greenOpacity, setGreenOpacity] = useState(0.8);
  const [redThreshold, setRedThreshold] = useState(20);
  const [greenThreshold, setGreenThreshold] = useState(20);
  const [currentZ, setCurrentZ] = useState(0);
  const [viewMode, setViewMode] = useState("mip"); // "mip" or "slice"
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  const dimensions = redVolume?.dimensions || greenVolume?.dimensions || [64, 64, 20];
  const [width, height, depth] = dimensions;
  const maxZ = Math.max(0, depth - 1);

  // Draw visualization on canvas
  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext("2d");
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;
    
    // Clear canvas
    ctx.fillStyle = "#020617";
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);
    
    if (!redVolume?.data && !greenVolume?.data) {
      ctx.fillStyle = "#64748b";
      ctx.font = "14px Manrope";
      ctx.textAlign = "center";
      ctx.fillText("No volume data", canvasWidth / 2, canvasHeight / 2);
      return;
    }
    
    const redData = redVolume?.data || [];
    const greenData = greenVolume?.data || [];
    const redThreshVal = (redThreshold / 100) * 255;
    const greenThreshVal = (greenThreshold / 100) * 255;
    
    // Calculate scale to fit canvas
    const scale = Math.min(canvasWidth / width, canvasHeight / height);
    const offsetX = (canvasWidth - width * scale) / 2;
    const offsetY = (canvasHeight - height * scale) / 2;
    
    // Create image data
    const imageData = ctx.createImageData(Math.ceil(width * scale), Math.ceil(height * scale));
    
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        let redVal = 0;
        let greenVal = 0;
        
        if (viewMode === "mip") {
          // Maximum Intensity Projection
          for (let z = 0; z < depth; z++) {
            const idx = z * width * height + y * width + x;
            if (redData[idx] !== undefined) redVal = Math.max(redVal, redData[idx]);
            if (greenData[idx] !== undefined) greenVal = Math.max(greenVal, greenData[idx]);
          }
        } else {
          // Single slice
          const idx = currentZ * width * height + y * width + x;
          redVal = redData[idx] || 0;
          greenVal = greenData[idx] || 0;
        }
        
        // Apply thresholds
        if (redVal < redThreshVal) redVal = 0;
        if (greenVal < greenThreshVal) greenVal = 0;
        
        // Apply visibility and opacity
        const finalRed = showRed ? Math.round(redVal * redOpacity) : 0;
        const finalGreen = showGreen ? Math.round(greenVal * greenOpacity) : 0;
        
        // Set pixels (upscaled)
        const startPx = Math.floor(x * scale);
        const startPy = Math.floor(y * scale);
        const endPx = Math.floor((x + 1) * scale);
        const endPy = Math.floor((y + 1) * scale);
        
        for (let py = startPy; py < endPy && py < imageData.height; py++) {
          for (let px = startPx; px < endPx && px < imageData.width; px++) {
            const pixelIdx = (py * imageData.width + px) * 4;
            imageData.data[pixelIdx] = finalRed;
            imageData.data[pixelIdx + 1] = finalGreen;
            imageData.data[pixelIdx + 2] = 0;
            imageData.data[pixelIdx + 3] = 255;
          }
        }
      }
    }
    
    ctx.putImageData(imageData, offsetX, offsetY);
    
    // Draw info text
    ctx.fillStyle = "#64748b";
    ctx.font = "12px JetBrains Mono";
    ctx.textAlign = "left";
    const modeText = viewMode === "mip" ? "MIP (Max Intensity)" : `Slice Z=${currentZ + 1}/${depth}`;
    ctx.fillText(modeText, 10, 20);
    ctx.fillText(`${width}×${height}×${depth}`, 10, 36);
  }, [redVolume, greenVolume, showRed, showGreen, redOpacity, greenOpacity, redThreshold, greenThreshold, currentZ, viewMode, width, height, depth]);

  useEffect(() => {
    drawCanvas();
  }, [drawCanvas]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const hasData = (redVolume?.data?.length > 0) || (greenVolume?.data?.length > 0);

  return (
    <motion.div
      ref={containerRef}
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      className="metric-card p-0 overflow-hidden"
      data-testid="volume-viewer"
    >
      {/* Canvas with Z-Slider on the left */}
      <div className="relative h-[500px] bg-[#020617] flex">
        {/* Z-Slice Slider - Left side, outside image (only in slice mode) */}
        {viewMode === "slice" && hasData && (
          <div className="flex flex-col items-center justify-center px-3 py-4 bg-secondary/30 border-r border-white/5">
            <span className="text-xs text-muted-foreground mb-2">Z</span>
            <div className="h-[350px] flex items-center">
              <Slider
                value={[currentZ]}
                onValueChange={([v]) => setCurrentZ(v)}
                max={maxZ}
                step={1}
                orientation="vertical"
                className="h-full [&_[role=slider]]:bg-red-500 [&_[role=slider]]:border-red-500"
                data-testid="z-slice-slider"
              />
            </div>
            <span className="font-mono text-xs mt-2 text-center">
              {currentZ + 1}<br/>
              <span className="text-muted-foreground">/{depth}</span>
            </span>
          </div>
        )}
        
        {/* Main canvas area */}
        <div className="flex-1 relative">
          <canvas
            ref={canvasRef}
            width={512}
            height={450}
            className="w-full h-full object-contain"
            style={{ imageRendering: "pixelated" }}
          />
          
          {/* View Mode Tabs */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2">
            <Tabs value={viewMode} onValueChange={setViewMode} className="w-auto">
              <TabsList className="glass">
                <TabsTrigger value="mip" className="text-xs gap-1">
                  <Layers className="w-3 h-3" />
                  MIP
                </TabsTrigger>
                <TabsTrigger value="slice" className="text-xs gap-1">
                  Z-Slice
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          
          {/* Floating Controls */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 glass rounded-full px-4 py-2 flex items-center gap-4">
            <button
              onClick={() => setShowRed(!showRed)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-colors ${
                showRed ? "bg-red-500/20 text-red-400" : "text-muted-foreground"
              }`}
              data-testid="toggle-red-channel"
            >
              {showRed ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              <span className="text-sm">Red</span>
            </button>
            
            <div className="w-px h-6 bg-white/10" />
            
            <button
              onClick={() => setShowGreen(!showGreen)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-colors ${
                showGreen ? "bg-green-500/20 text-green-400" : "text-muted-foreground"
              }`}
              data-testid="toggle-green-channel"
            >
              {showGreen ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              <span className="text-sm">Green</span>
            </button>
            
            <div className="w-px h-6 bg-white/10" />
            
            <button
              onClick={toggleFullscreen}
              className="p-1.5 rounded-full hover:bg-white/10 transition-colors"
              data-testid="fullscreen-btn"
            >
              <Maximize2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Side Controls */}
      <div className="p-4 border-t border-white/5 space-y-4">
        <div className="grid grid-cols-2 gap-6">
          {/* Red Channel Controls */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <Label className="text-sm">Red Channel</Label>
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Opacity</span>
                <span className="font-mono">{Math.round(redOpacity * 100)}%</span>
              </div>
              <Slider
                value={[redOpacity * 100]}
                onValueChange={([v]) => setRedOpacity(v / 100)}
                max={100}
                step={5}
                className="[&_[role=slider]]:bg-red-500"
                data-testid="red-opacity-slider"
              />
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Threshold</span>
                <span className="font-mono">{redThreshold}%</span>
              </div>
              <Slider
                value={[redThreshold]}
                onValueChange={([v]) => setRedThreshold(v)}
                max={100}
                step={5}
                className="[&_[role=slider]]:bg-red-500"
                data-testid="red-threshold-slider"
              />
            </div>
          </div>

          {/* Green Channel Controls */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500" />
              <Label className="text-sm">Green Channel</Label>
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Opacity</span>
                <span className="font-mono">{Math.round(greenOpacity * 100)}%</span>
              </div>
              <Slider
                value={[greenOpacity * 100]}
                onValueChange={([v]) => setGreenOpacity(v / 100)}
                max={100}
                step={5}
                className="[&_[role=slider]]:bg-green-500"
                data-testid="green-opacity-slider"
              />
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Threshold</span>
                <span className="font-mono">{greenThreshold}%</span>
              </div>
              <Slider
                value={[greenThreshold]}
                onValueChange={([v]) => setGreenThreshold(v)}
                max={100}
                step={5}
                className="[&_[role=slider]]:bg-green-500"
                data-testid="green-threshold-slider"
              />
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
